import { defineStore } from 'pinia';
import { resolveLegacyNamespace } from '@/stores/legacyBridge';

interface InactivityTrackerState {
  /** Interval handle for the inactivity poll; null when no timer is running */
  timeoutTimer: ReturnType<typeof setInterval> | null;
  /** Epoch millis when the user counts as inactive; null when no timer runs */
  timestamp: number | null;
  timeoutDuration: number; // in milliseconds
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/inactivityTracker.ts` (namespace
 * `inactivityTracker`). In-memory only — no `vuexPersist` option (matches STORES.md).
 *
 * The legacy getter `timeoutTimer` was a pure pass-through of the state prop of the same
 * name. In Pinia a getter cannot share a name with a state prop (the getter shadows the
 * state and resolves to `undefined`), so it is dropped — actions read `this.timeoutTimer`
 * from state directly, and consumers read `store.timeoutTimer`.
 */
export const useInactivityTrackerStore = defineStore('inactivityTracker', {
  state: (): InactivityTrackerState => ({
    timeoutTimer: null,
    timestamp: null,
    timeoutDuration: 0, // in milliseconds
  }),
  actions: {
    restartTimeoutTimer() {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
      }

      if (this.timeoutDuration > 0) {
        // the timestamp is the current date and time plus the timeout duration
        this.timestamp = new Date().getTime() + this.timeoutDuration;
        this.timeoutTimer = setInterval(() => {
          // if the timestamp is in the past, the user has been inactive for too long
          if ((this.timestamp as number) < new Date().getTime()) {
            this.timestamp = null;
            clearTimeout(this.timeoutTimer as ReturnType<typeof setInterval>);

            // `livechat` hasn't been converted to Pinia yet. Resolve it through the legacy
            // bridge registry instead of a direct `useLivechatStore()` import so this file
            // type-checks and behaves correctly today, and starts working for real the moment
            // a later phase registers `livechat` there — no change needed here.
            // TODO: Phase 2 replaces this with `useLivechatStore().setAgentInactive()` once
            // `src/stores/livechat.ts` exists.
            const livechatStore = resolveLegacyNamespace('livechat');
            livechatStore?.setAgentInactive?.(null);
          }
        }, 1000);
      }
    },

    removeTimeoutTimer() {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
        this.timestamp = null;
      }
    },

    /**
     * Converted from legacy mutation `setTimeoutTimer` (no matching legacy action existed).
     */
    setTimeoutTimer(timer: ReturnType<typeof setInterval> | null) {
      this.timeoutTimer = timer;
    },
    /**
     * Converted from legacy mutation `setTimeoutDuration` (no matching legacy action existed).
     */
    setTimeoutDuration(payload: { timeoutDuration: number }) {
      this.timeoutDuration = payload.timeoutDuration;
    },
  },
});
